import {useDispatch, useSelector} from "react-redux";
import {useQueryClient} from "@tanstack/react-query";
import useOverwolfInterop, { isOnOWKey } from "@/hooks/useOverwolfInterop";
import {logout} from "@/redux/auth/authActions";
import type {Wallet} from "thirdweb/wallets";
import getConfig from "@/utils/getConfig";
import { TokensApi } from "@devour/client";
import {IStore} from "@/redux/defaultStore";

/**
 * Logout is invoked from global nav chrome (sidebar, account menu, PageWrapper),
 * which no longer mounts the ThirdwebProvider — so this hook must NOT call
 * thirdweb hooks (useActiveWallet / useDisconnect) or they'd throw "no provider"
 * on every page. The wallet session lives in localStorage
 * (thirdweb:active-wallet-id / connected-wallet-ids), which `localStorage.clear()`
 * below wipes, so any active wallet is fully disconnected on the next load without
 * the SDK. When a wallet surface passes its live `instancedWallet`, we disconnect
 * it directly via the Wallet instance's own `.disconnect()` — a plain method, no
 * hook or runtime thirdweb import (the `Wallet` type here is erased at build).
 */
export default function useOnLogout() {
    const { sendMessageToOW } = useOverwolfInterop();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    async function devourLogout(instancedWallet?: Wallet) {
        // Logout initiated
        try {
            if (fullToken) {
                try {
                    await new TokensApi(getConfig()).deleteTokens({
                        tokenBody: {
                            token: fullToken.token,
                        },
                    });
                } catch (error) {
                    // Log error but continue with logout process
                    console.error("Failed to delete token from server:", error);
                }
            }

            /*
             * Clear React Query cache BEFORE dispatching logout. Auth-state
             * watchers re-run queries the moment fullToken becomes null; if the
             * cache still holds the previous user's entries, those subscribers
             * would briefly serve stale data before a fresh query resolves.
             */
            queryClient.clear();

            dispatch(logout());
            sendMessageToOW({type: "de:logout"});

            if (instancedWallet) {
                try {
                    await instancedWallet.disconnect();
                } catch (error) {
                    console.error("Failed to disconnect instanced wallet:", error);
                }
            }

            /*
             * Delete storage data — this also clears thirdweb's persisted wallet
             * session (thirdweb:active-wallet-id / connected-wallet-ids), so an
             * active wallet is fully disconnected on next load without the SDK.
             */
            const isOnOverwolf = sessionStorage.getItem(isOnOWKey);
            localStorage.clear();
            sessionStorage.clear();
            if (isOnOverwolf) {
                sessionStorage.setItem(isOnOWKey, isOnOverwolf);
            }
        } catch (error) {
            console.error("Failed to logout:", error);
            // Re-throw to let calling code handle if needed
            throw new Error("Failed to logout");
        }
    }

    return {devourLogout};
}
